import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ACLService, DelonACLModule } from '@delon/acl';
import { _HttpClient } from '@delon/theme';
import { Subscription } from 'rxjs';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { FcwidgetService } from 'src/app/core/util/fcwidget.service';

import { ChatbotTriggerService } from './chatbot-trigger.service';
import { TextToSpeechService } from './text-to-speech.service';
import { Router } from '@angular/router';
import { USER_AVATAR_URL } from '@core';

@Component({
  selector: 'header-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    NzAvatarModule,
    NzIconModule,
    NzDropDownModule,
    NzMenuModule,
    DelonACLModule
  ]
})
export class HeaderChatbotComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('chatListContainer') list?: ElementRef<HTMLDivElement>;
  @ViewChild('optionMenu') optionMenu?: ElementRef;

  readonly userAvatarUrl = USER_AVATAR_URL;

  private listener!: Subscription;
  messages: any[] = [];
  model: any = {};
  isWaiting: boolean = false;
  isCompatible: boolean = 'webkitSpeechRecognition' in window;
  isAudioRecording = false;
  isMute = false;
  availableOptions: any[] = [];
  defaultOptions = [
    {
      label: 'Bank',
      acceptable_value: ['Bank', 'bank balance', 'one'],
      value: 'Bank',
      cancel: false
    },
    {
      label: 'Profit',
      acceptable_value: ['Profit', 'two'],
      value: 'Profit',
      cancel: false
    },
    {
      label: 'Sales',
      acceptable_value: ['Sales', 'three'],
      value: 'Sales',
      cancel: false
    }
  ];
  closeOption = [
    {
      label: 'No, thanks',
      acceptable_value: ['No'],
      value: 'No, thanks',
      cancel: false,
      triggerEnd: true
    }
  ];

  get isMobileAgent() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(
      navigator.userAgent
    );
  }

  constructor(
    private tts: TextToSpeechService,
    private listenSrv: ChatbotTriggerService,
    private http: _HttpClient,
    private acl: ACLService,
    private fcWidget: FcwidgetService,
    private router: Router
  ) {
    this.tts.init();
  }

  ngOnInit(): void {
    this.listener = this.listenSrv.$triggerEvent.subscribe(() => {
      setTimeout(() => this.toggleChatbot(), 0);
    });
  }

  ngOnDestroy(): void {
    this.listener.unsubscribe();
    this.tts.stop();
  }

  openAiAssistant() {
    const chatbot = document.getElementById('chatbot-window');
    if (chatbot?.classList.contains('visible')) {
      chatbot.classList.toggle('visible');
    }
    this.fcWidget.openWithUserContext();
  }

  cdr = inject(ChangeDetectorRef);
  toggleChatbot() {
    // Close chat if it is open

    this.fcWidget.close();

    this.http
      .get(`api/userdata?keys=chatbot-mute&_silent=true`)
      .subscribe((res: any) => {
        if (res && res['chatbot-mute'] === 'true') {
          this.isMute = true;
        }

        const chatbot = document.getElementById('chatbot-window');
        if (this.tts) this.tts.isInitiated(this.isMute);

        if (!chatbot?.classList.contains('visible')) {
          this.http.get('api/Chatbot/Initialize').subscribe((res: any) => {
            const chats = res.chatRecords;

            this.messages = [...this.filterMsg(chats)];
            this.model = res;

            this.populateOptions(res.context.message.options);
            this.chatbotSpeak(res);
            this.cdr.markForCheck();
            this.scrollToBottom(false);
          });
        } else {
          this.tts.stop();
        }

        chatbot!.classList.toggle('visible');
      });
  }

  send(option: any = null) {
    if (!option.label) return;
    this.closeOptionBar(true);

    this.isWaiting = true;
    this.messages.push({
      isMachine: false,
      text: option.label,
      time: new Date()
    });

    // Populate option back to server
    this.model.context.message.answer = option.value;

    // Send request to server
    this.http
      .post('api/Chatbot?_silent=true', this.model)
      .subscribe((res: any) => {
        this.isWaiting = false;
        this.messages = this.filterMsg(res.chatRecords);
        this.model = res;

        if (res.downloadUrl) {
          window.open(res.downloadUrl, '_blank');
        }

        this.populateOptions(res.context.message.options);
        this.closeOptionBar(false);
        this.chatbotSpeak(res);

        // Explicitly trigger change detection
        this.cdr.markForCheck();
        this.scrollToBottom();
      });

    this.scrollToBottom();
  }

  //----------| Behavioural Code |----------//
  scrollToBottom(isSmooth: boolean = true) {
    setTimeout(() => {
      const element = this.list?.nativeElement;
      if (!element) return;

      if (isSmooth) {
        const maxScroll = this.list?.nativeElement.scrollHeight;
        element.scrollTo({ top: maxScroll, behavior: 'smooth' });
      } else {
        element.scrollTop = element.scrollHeight;
      }
    }, 100);
  }

  toggleSpeaker() {
    this.isMute = !this.isMute;
    this.http
      .post('api/userdata?_silent=true', { 'chatbot-mute': `${this.isMute}` })
      .subscribe(() => {});
    if (this.tts) this.tts.changeSpeakingMode(this.isMute);
  }

  filterMsg(msgs: any) {
    let filteredMsgs = [...msgs]; // Create a new array
    if (!this.acl.can('m1.1')) {
      filteredMsgs = filteredMsgs.map((o: any) => ({
        ...o,
        text: o.text
          .replaceAll('Bank\n\t Profit\n\t ', '')
          .replaceAll('Bank, Profit or', '')
      }));
    }
    return filteredMsgs;
  }

  private chatbotSpeak(res: any) {
    const lastRecord = res.chatRecords[res.chatRecords.length - 1];
    if (lastRecord?.isMachine) {
      const message = lastRecord.text;
      if (this.tts) this.tts.speak(message);
    }
  }

  private populateOptions(options: any) {
    let selections = Object.keys(options).map(key => ({
      label: key,
      value: options[key]
    }));

    if (!this.acl.can('m1.1')) {
      selections = selections.filter(
        (o: any) => !['Bank', 'Profit'].includes(o.label)
      );
    }

    this.availableOptions = [...selections]; // Create a new array
    this.cdr.markForCheck(); // Trigger change detection
  }

  private closeOptionBar(isClose: boolean) {
    if (this.optionMenu) {
      var element = this.optionMenu.nativeElement;

      if (isClose) {
        element.classList.add('hidden');
      } else {
        element.classList.remove('hidden');
      }
    }
  }

  ngAfterViewInit(): void {}

  //----------| Speech Recognition Code |----------//
  // toggleAudioRecording() {
  //   if(this.isMobileAgent == false) {
  //     if (!this.isAudioRecording) {
  //       this.isAudioRecording = true;
  //       this.speechToTexts.stop();
  //       this.speechToTexts.start();
  //     } else {
  //       this.isAudioRecording = false;
  //       this.speechToTexts.stop();

  //       if (this.speechToTexts.text){
  //         var text = this.speechToTexts.text.trim();
  //         if (text) {
  //           this.send(this.speechToTexts.text, false);
  //         }
  //       }
  //     }
  //   }
  // }

  // touchRecord(e: any) {
  //   if (e.type === "touchstart") {
  //     this.isAudioRecording = true;
  //     this.speechToTexts.stop();
  //     this.speechToTexts.start();
  //   }
  //   else if (e.type === "touchend") {
  //     this.isAudioRecording = false;
  //     this.speechToTexts.stop();

  //     if (this.speechToTexts.text){
  //       var text = this.speechToTexts.text.trim();
  //       if (text) {
  //         this.send(this.speechToTexts.text, false);
  //       }
  //     }
  //   }
  // }
}
